import { useEffect, useState } from "react";
import { colorFor } from '../lib/definitions/palette';
import { freshnessOf, FRESHNESS_LABEL } from '../lib/definitions/health';
import { PROTOCOL_LABEL, UNKNOWN_PROTOCOL_LABEL, roleInfo, packetCategoryInfo } from '../lib/definitions/protocols';
import { resolveNodeProtocol } from "../lib/nodeProtocol";
import { lastHeardText } from "../lib/packetFeed";
import { relativeTime } from "../lib/format";
import { useAppData } from "../state/AppData";
import { navigate } from "../lib/router";
import { useAuth } from "../state/AuthContext";
import { RetireNodeButton } from "./RetiredNodesList";
import { ObserverBadge } from "./ObserverBadge";
import { NodeTagsControl } from "./NodeTags";
import type { NodeDetail } from "../api/types";

export function NodeDetailPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const { nodes, loadNodeDetail, effectiveTheme, select, observerTrust } = useAppData();
  const { isAdmin, status: authStatus } = useAuth();
  const base = nodes.get(id);
  const [detail, setDetail] = useState<NodeDetail | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setLoadError(false);
    loadNodeDetail(id).then((d) => {
      if (cancelled) return;
      if (d) setDetail(d);
      else setLoadError(true);
    });
    return () => {
      cancelled = true;
    };
  }, [id, loadNodeDetail]);

  const node = detail ?? base;
  if (!node) {
    return (
      <div className="detail-panel">
        <div className="empty-state">Node {id} not found.</div>
        <button className="close-btn" onClick={onClose}>Close</button>
      </div>
    );
  }

  const protocol = resolveNodeProtocol(node);
  const role = protocol === "unknown" ? null : roleInfo(protocol, node.role);
  const fresh = freshnessOf(node.last_heard);
  const name = node.long_name || node.short_name || String(node.id);

  return (
    <div className="detail-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <h3>{name}</h3>
        <button className="close-btn" onClick={onClose}>✕</button>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
        <span className="badge" style={{ background: "var(--ink-muted)" }}>
          {protocol === "unknown" ? UNKNOWN_PROTOCOL_LABEL : PROTOCOL_LABEL[protocol]}
        </span>
        {role && (
          <span className="badge" style={{ background: colorFor(role.color, "light") }}>
            {role.label}
          </span>
        )}
        <span className="badge" style={{ background: "var(--ink-muted)" }}>
          {FRESHNESS_LABEL[fresh]}
        </span>
      </div>

      <div className="detail-row">
        <span className="k">ID</span>
        <span>{node.id}</span>
      </div>
      <div className="detail-row">
        <span className="k">Short name</span>
        <span>{node.short_name ?? "--"}</span>
      </div>
      <div className="detail-row">
        <span className="k">Last heard</span>
        <span>{lastHeardText(relativeTime(node.last_heard), detail?.last_heard_source_label ?? node.last_heard_source_label)}</span>
      </div>
      <div className="detail-row">
        <span className="k">Position</span>
        <span>{node.lat != null && node.lon != null ? `${node.lat.toFixed(4)}, ${node.lon.toFixed(4)}` : "unknown"}</span>
      </div>
      <div className="detail-row">
        <span className="k">SNR</span>
        <span>{node.snr ?? "--"}</span>
      </div>
      <div className="detail-row">
        <span className="k">Hops</span>
        <span>{node.hops ?? "--"}</span>
      </div>
      <NodeTagsControl
        protocol={protocol}
        nativeId={id}
        tags={base?.tags ?? detail?.tags ?? []}
        canEdit={protocol !== "unknown" && (authStatus === "authenticated" || authStatus === "not_required")}
      />
      <div className="detail-row">
        <span className="k">Heard by</span>
        <span style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {node.observer_ids?.length
            ? node.observer_ids.map((oid) => <ObserverBadge key={oid} observerId={oid} trustTier={observerTrust.get(oid)} compact />)
            : "--"}
        </span>
      </div>

      <button className="full-detail-link" onClick={() => navigate(`#/nodes/${encodeURIComponent(id)}`)}>
        Full detail →
      </button>

      {isAdmin && protocol !== "unknown" && (
        <div style={{ marginTop: 8 }}>
          <RetireNodeButton protocol={protocol} nativeId={id} onDone={onClose} />
        </div>
      )}

      {loadError && (
        <div style={{ marginTop: 8, fontSize: 11, color: "var(--ink-muted)" }}>
          Extended detail (recent packets, heard-by) unavailable — showing roster data only.
        </div>
      )}

      {detail?.heard_by && detail.heard_by.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div className="panel-header" style={{ padding: "0 0 4px" }}>Heard by</div>
          {detail.heard_by.map((h, i) => {
            const oid = typeof h === "string" ? h : (h as any).observer_id;
            const count = typeof h === "string" ? undefined : (h as any).count;
            return (
              <div className="heard-by-row" key={`${oid}-${i}`}>
                <ObserverBadge observerId={oid} trustTier={observerTrust.get(oid)} compact />
                <span>{count ?? ""}</span>
              </div>
            );
          })}
        </div>
      )}

      {detail?.corescope_heard_by && detail.corescope_heard_by.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div className="panel-header" style={{ padding: "0 0 4px" }}>Heard by (CoreScope)</div>
          {detail.corescope_heard_by.map((c) => (
            <div className="heard-by-row" key={`cs-${c.station_id}`}>
              <span>{c.name || c.station_id}</span>
              <span>
                {c.count}
                {c.best_snr != null ? ` · ${c.best_snr} dB` : ""}
              </span>
            </div>
          ))}
        </div>
      )}

      {detail?.recent_packets && detail.recent_packets.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div className="panel-header" style={{ padding: "0 0 4px" }}>Recent packets</div>
          {detail.recent_packets.map((p, i) => (
            <div
              className="recent-packet-row"
              key={`${p.hash || p.id || "pkt"}-${i}`}
              onClick={() => select({ kind: "packet", packet: p })}
            >
              <span>{packetCategoryInfo(p.protocol, p.type).label}</span>
              <span>{relativeTime(p.ts)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
